'use client';

import { c as _c } from "react/compiler-runtime";
import { startTransition } from 'react';
import './DashboardClient.css';
import Hijackable from '@/features/ministry/Hijackable';
import NotificationToggle from '@/features/notifications/NotificationToggle';
import LastUpdated from '@/shared/components/LastUpdated';
import EventCard, { computeFrontier } from '@/features/galaxy/EventCard';
import { attackForecast, sectorForecast } from '@/features/dashboard/attackForecast.mjs';
import { eventForecast } from '@/features/dashboard/eventForecast.mjs';
import DefeatedCard from '@/features/galaxy/DefeatedCard';
import { highlightSector, clearSectorHighlight } from '@/features/galaxy/sectorLink.mjs';
import FactionTabs from '@/shared/components/FactionTabs';
import RegionsViewToggle from '@/features/dashboard/RegionsViewToggle';
import StatGrid from '@/features/stats/StatGrid';
import { useLiveDataContext } from '@/shared/providers/LiveDataContext.mjs';
import { evaluateProgress } from '@/features/stats/evaluateProgress.mjs';
import { sortEventsByRecent } from '@/shared/utils/game/eventFilters.mjs';
import { HOMEWORLD_REGION } from '@/shared/enums/worlds.mjs';
import { CAMPAIGN_STATUS, EVENT_TYPE, EVENT_STATUS } from '@/shared/enums/events.mjs';
import { computePulseDelays } from '@/shared/utils/game/pulseDelays.mjs';
import ComponentErrorBoundary from '@/shared/components/ComponentErrorBoundary';
import { usePersistedState } from '@/shared/hooks/usePersistedState.mjs';
import { FACTION_KEY } from '@/shared/preferences/faction.mjs';
import { REGIONS_VIEW_KEY } from '@/shared/preferences/regionsView.mjs';
const factionIndices = [0, 1, 2];

/**
 * Shape of a single live event as returned by `getCampaign()`'s
 * `h1_event.findMany` select. `sortEventsByRecent` is typed to its sort
 * key alone (`{ start_time }`), so we restate the full shape here to read
 * the other fields off each event without losing them to that narrow type.
 * Unlike the optional-field `Event` typedef in enums/events.mjs, the live
 * select always returns these columns as concrete values.
 *
 * @typedef {object} LiveEvent
 * @property {import('@/shared/enums/events.mjs').EventType} type - Event type (`defend` or `attack`).
 * @property {number} start_time - Unix-seconds event start.
 * @property {number} end_time - Unix-seconds event end.
 * @property {number} region - Galaxy-map region (sector 1-10, or 11 for homeworld).
 * @property {number} enemy - Faction index (0-2).
 * @property {number} points - Current event progress points.
 * @property {number} points_max - Points required to complete the event.
 * @property {import('@/shared/enums/events.mjs').EventStatus} status - Event status (active/success/fail).
 */

/**
 * Hover props for a region card's `<li>`: `data-*` attributes that key the
 * card to its galaxy-map sector, plus handlers that light the matching map
 * area on hover (faction territory faint, active sector strong). The `data-*`
 * attributes also leave the card findable for a future map → card reverse
 * highlight (#185).
 *
 * @param {number} factionIndex - 0-2 faction, or 3 for Super Earth
 * @param {number | null} [sector] - Active sector (1-11; 0 for Super Earth);
 *   omitted for a defeated faction with no single active sector
 * @returns {object} Props to spread onto the card's `<li>`
 */
function sectorHoverProps(factionIndex, sector = null) {
  return {
    'data-faction-index': factionIndex,
    ...(sector != null && {
      'data-sector': sector
    }),
    onMouseEnter: () => highlightSector(factionIndex, sector),
    onMouseLeave: clearSectorHighlight
  };
}

/**
 * @param {object} props - Component props.
 * @param {string} [props.initialFaction] - Server-read active faction tab.
 * @param {string} [props.initialRegionsView] - Server-read regions view mode.
 * @param {import('@/features/stats/StatGrid').PlayersAvg24h | null} [props.playersAvg24h] - 24h player baselines.
 * @param {import('@/features/stats/StatGrid').KillsTrend | null} [props.killsTrend] - 24h/48h kill baselines.
 */
export default function DashboardClient(t0) {
  const $ = _c(62);
  const {
    initialFaction: t1,
    initialRegionsView: t2,
    playersAvg24h: t3,
    killsTrend: t4
  } = t0;
  const initialFaction = t1 === undefined ? "global" : t1;
  const initialRegionsView = t2 === undefined ? "sector" : t2;
  const playersAvg24h = t3 === undefined ? null : t3;
  const killsTrend = t4 === undefined ? null : t4;
  const {
    data,
    mapState
  } = useLiveDataContext();
  const [faction, setFaction] = usePersistedState(FACTION_KEY, initialFaction);
  const [regionsView, setRegionsView] = usePersistedState(REGIONS_VIEW_KEY, initialRegionsView);
  if (!data) {
    let t5;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="gutters flex min-h-full w-full flex-col items-center justify-center py-12"><Hijackable as="h1" category="heading" text="SIGNAL LOST" /><p>Communication with Super Earth High Command has been disrupted. This is not cause for alarm. Remain calm and await further instructions.</p></div>;
      $[0] = t5;
    } else {
      t5 = $[0];
    }
    return t5;
  }
  let T0;
  let events;
  let t10;
  let t11;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== data || $[2] !== mapState || $[3] !== regionsView || $[4] !== setRegionsView) {
    events = sortEventsByRecent(data?.events);
    const pulseDelays = computePulseDelays(data?.events);
    const isCampaignView = regionsView === "campaign";
    const superEarthDefendEvent = events?.find(_temp);
    const seDefenderIndex = superEarthDefendEvent?.enemy ?? null;
    const renderFrontierCard = function renderFrontierCard(index) {
      if (index === seDefenderIndex && superEarthDefendEvent) {
        return <li key={`frontier-${index}`} {...sectorHoverProps(3, 0)} data-attacker-index={index}><EventCard action="defending" barLabel="SUPER_EARTH_DEFENSE" region="Super Earth" percent={superEarthDefendEvent.points / superEarthDefendEvent.points_max * 100} points={superEarthDefendEvent.points} pointsMax={superEarthDefendEvent.points_max} factionIndex={index} pace={evaluateProgress(superEarthDefendEvent)} endTime={superEarthDefendEvent.end_time} eventEta={eventForecast(superEarthDefendEvent, Math.floor(Date.now() / 1000))} pulseDelay={pulseDelays.get(`${index}-0`)} factionMap={undefined} /></li>;
      }
      const campaignData = data.status?.find(l => l.enemy === index);
      if (campaignData?.status === CAMPAIGN_STATUS.DEFEATED) {
        const factionEvents = events?.filter(e_0 => e_0.enemy === index) ?? [];
        const defeatEvent = factionEvents.find(_temp2);
        const earliestStart = factionEvents.reduce(_temp3, Infinity);
        return <li key={`frontier-${index}`} {...sectorHoverProps(index)}><DefeatedCard factionIndex={index} startTime={earliestStart !== Infinity ? earliestStart : null} endTime={defeatEvent?.end_time ?? null} view={regionsView} /></li>;
      }
      const frontier = computeFrontier(campaignData, mapState[index]);
      if (!frontier) {
        return null;
      }
      const isDefending = frontier.event === EVENT_STATUS.ACTIVE;
      const activeEvent = isDefending ? events?.find(e_3 => e_3.enemy === index && e_3.type === EVENT_TYPE.DEFEND && e_3.status === EVENT_STATUS.ACTIVE) : null;
      const showEvent = !isCampaignView && activeEvent;
      const metaPoints = isCampaignView ? campaignData.points : isDefending && activeEvent ? activeEvent.points : frontier.points;
      const metaPointsMax = isCampaignView ? campaignData.points_max : isDefending && activeEvent ? activeEvent.points_max : frontier.pointsMax;
      return <li key={`frontier-${index}`} {...sectorHoverProps(index, frontier.sector)}><EventCard action={isDefending ? "defending" : "capturing"} barLabel={isCampaignView ? "FACTION_PROGRESS" : isDefending ? "CAPITAL_DEFENSE" : "SECTOR_PROGRESS"} region={frontier.region} percent={showEvent ? activeEvent.points / activeEvent.points_max * 100 : frontier.percent} points={metaPoints} pointsMax={metaPointsMax} factionIndex={index} pace={showEvent ? evaluateProgress(activeEvent) : null} endTime={showEvent ? activeEvent.end_time : undefined} alert={isCampaignView && !!activeEvent} etaForecast={isCampaignView ? attackForecast(data, index, Math.floor(Date.now() / 1000)) : sectorForecast(data, index, Math.floor(Date.now() / 1000))} eventEta={showEvent ? eventForecast(activeEvent, Math.floor(Date.now() / 1000)) : null} pulseDelay={activeEvent ? pulseDelays.get(`${activeEvent.enemy}-${activeEvent.region}`) : undefined} view={regionsView} factionMap={mapState[index]} /></li>;
    };
    const renderHomeworldCard = function renderHomeworldCard(index_0) {
      if (index_0 === seDefenderIndex) {
        return null;
      }
      const homeworld = mapState[index_0]?.[HOMEWORLD_REGION];
      if (homeworld?.event !== EVENT_STATUS.ACTIVE) {
        return null;
      }
      const attackEvent = events?.find(e_4 => e_4.enemy === index_0 && e_4.type === EVENT_TYPE.ATTACK && e_4.status === EVENT_STATUS.ACTIVE);
      return <li key={`attack-${index_0}`} {...sectorHoverProps(index_0, HOMEWORLD_REGION)}><EventCard action="capturing" barLabel="HOMEWORLD_ASSAULT" region={homeworld.region} percent={attackEvent ? attackEvent.points / attackEvent.points_max * 100 : homeworld.percent} points={attackEvent ? attackEvent.points : homeworld.points} pointsMax={attackEvent ? attackEvent.points_max : homeworld.points_max} factionIndex={index_0} pace={attackEvent ? evaluateProgress(attackEvent) : null} endTime={attackEvent?.end_time} eventEta={attackEvent ? eventForecast(attackEvent, Math.floor(Date.now() / 1000)) : null} pulseDelay={attackEvent ? pulseDelays.get(`${attackEvent.enemy}-${attackEvent.region}`) : undefined} view={attackEvent ? "sector" : regionsView} factionMap={!attackEvent && isCampaignView ? mapState[index_0] : undefined} /></li>;
    };
    let t12;
    let t13;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Hijackable as="h1" category="heading" text="Track Managed Democracy Across the Galaxy" className="font-display text-body text-primary" />;
      t13 = <p className="mb-0! text-small text-text-muted">Don't miss a moment of the action! Follow the Helldivers' campaign progress as they battle the Bugs, Cyborgs, and Illuminate for peace, liberty, and managed democracy.</p>;
      $[14] = t12;
      $[15] = t13;
    } else {
      t12 = $[14];
      t13 = $[15];
    }
    let t14;
    if ($[16] !== data.last_updated) {
      t14 = <LastUpdated lastUpdated={data.last_updated} />;
      $[16] = data.last_updated;
      $[17] = t14;
    } else {
      t14 = $[17];
    }
    let t15;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <NotificationToggle />;
      $[18] = t15;
    } else {
      t15 = $[18];
    }
    if ($[19] !== t14) {
      t11 = <section className="flex flex-col gap-2">{t12}{t13}<div className="flex flex-col items-start gap-2">{t14}{t15}</div></section>;
      $[19] = t14;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    t9 = "flex flex-col gap-2";
    const t16 = `Season ${data.season}`;
    let t17;
    if ($[21] !== t16) {
      t17 = <Hijackable as="h2" category="heading" text={t16} />;
      $[21] = t16;
      $[22] = t17;
    } else {
      t17 = $[22];
    }
    let t18;
    if ($[23] !== regionsView || $[24] !== setRegionsView) {
      t18 = <RegionsViewToggle value={regionsView} onChange={setRegionsView} />;
      $[23] = regionsView;
      $[24] = setRegionsView;
      $[25] = t18;
    } else {
      t18 = $[25];
    }
    if ($[26] !== t17 || $[27] !== t18) {
      t10 = <div className="flex items-center justify-between gap-2">{t17}{t18}</div>;
      $[26] = t17;
      $[27] = t18;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    T0 = ComponentErrorBoundary;
    t8 = "Regions";
    t5 = "sector-grid list-none p-0";
    t6 = factionIndices.map(renderFrontierCard);
    t7 = factionIndices.map(renderHomeworldCard);
    $[1] = data;
    $[2] = mapState;
    $[3] = regionsView;
    $[4] = setRegionsView;
    $[5] = T0;
    $[6] = events;
    $[7] = t10;
    $[8] = t11;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    T0 = $[5];
    events = $[6];
    t10 = $[7];
    t11 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t12;
  if ($[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t12 = <ul className={t5}>{t6}{t7}</ul>;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== T0 || $[34] !== t12 || $[35] !== t8) {
    t13 = <T0 name={t8}>{t12}</T0>;
    $[33] = T0;
    $[34] = t12;
    $[35] = t8;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t10 || $[38] !== t13 || $[39] !== t9) {
    t14 = <section className={t9}>{t10}{t13}</section>;
    $[37] = t10;
    $[38] = t13;
    $[39] = t9;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Hijackable as="h2" category="heading" text="Stats" />;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== setFaction) {
    t16 = id => startTransition(() => setFaction(id));
    $[42] = setFaction;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== faction || $[45] !== t16) {
    t17 = <div className="flex items-center justify-between gap-2">{t15}<FactionTabs active={faction} onChange={t16} /></div>;
    $[44] = faction;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== data.season_duration || $[48] !== data.status || $[49] !== data.war_start || $[50] !== events || $[51] !== faction || $[52] !== killsTrend || $[53] !== playersAvg24h) {
    t18 = <StatGrid live={data.status} faction={faction} events={events} playersAvg24h={playersAvg24h} killsTrend={killsTrend} seasonDuration={data.season_duration} warStart={data.war_start} />;
    $[47] = data.season_duration;
    $[48] = data.status;
    $[49] = data.war_start;
    $[50] = events;
    $[51] = faction;
    $[52] = killsTrend;
    $[53] = playersAvg24h;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t17 || $[56] !== t18) {
    t19 = <section className="flex flex-col gap-2"><ComponentErrorBoundary name="Stats">{t17}{t18}</ComponentErrorBoundary></section>;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== t11 || $[59] !== t14 || $[60] !== t19) {
    t20 = <>{t11}{t14}{t19}</>;
    $[58] = t11;
    $[59] = t14;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  return t20;
}
function _temp3(min, e_2) {
  return e_2.start_time < min ? e_2.start_time : min;
}
function _temp2(e_1) {
  return e_1.type === EVENT_TYPE.ATTACK && e_1.status === EVENT_STATUS.SUCCESS;
}
function _temp(e) {
  return e.type === EVENT_TYPE.DEFEND && e.region === 0 && e.status === EVENT_STATUS.ACTIVE;
}